import {ClosingFileService} from "@/services/closing_file";
import {API_ENDPOINTS} from "@/constants/common";
import {
  COLUMNS_META,
  COMMON_HEADINGS,
  DETAIL_TABLE_NAMES,
  MANUAL_CONTROL_DETAIL_COLUMNS,
  MANUAL_CONTROL_COLUMNS,
  HIDDEN_DETAIL_COLUMNS,
  TABLE_FILTERS,
  ECART_COLUMNS,
  getColumnsMeta,
  calculateEcart,
  detailsRestricted,
  performManualControl,
  ACCOUNT,
} from "@/constants/closingFileTables";
import {request} from "@/utils/request";

function safeParseFloat(input) {
  if (input === '') return 0
  else return parseFloat(input)
}

export const TestResultMixin = {
  data() {
    return {
      detailsRestricted,
      COLUMNS_META,
      TABLE_FILTERS,
      API_ENDPOINTS,
      MANUAL_CONTROL_COLUMNS,
      ECART_COLUMNS,
      MANUAL_CONTROL_DETAIL_COLUMNS,
      HIDDEN_DETAIL_COLUMNS,
      DETAIL_TABLE_NAMES,
      testResults: null,
      restrictedDetailsIndex: null,
      manualControlColumns: [],
      manualControl: {},
      detailTables: {},
      periodDependent: true,
      showDetails: false,
      selectedRow: null,
      isLoading: false,
      totalRow: null,
      totalCode: "Total",
      column_name: null,
    };
  },
  computed: {
    testId() {
      return this.$route.query.tab;
    },
    entityId() {
      return this.$route.params.entity_id;
    },
    quarter_name() {
      return this.periodDependent ? this.$route.params.quarter_name : "ALL";
    },
    hasTotalRow() {
      return this.testId === "J1";
    },
    totalRowKey() {
      return `test_${this.testId.toLowerCase()}`;
    },
    totalRowComputed() {
      if (!this.hasTotalRow) {
        return null;
      }
      const manualData = this.manualControl?.["0"] || [];
      const manualSums = {};
      Object.values(manualData).forEach((entry) =>
        Object.entries(entry).forEach(([key, value]) => {
          value = parseFloat(value);
          if (!isNaN(value)) {
            if (!manualSums[key]) {
              manualSums[key] = 0;
            }
            manualSums[key] += value;
          }
        })
      );
      const controlFn = performManualControl[this.testId];
      const row = {...this.totalRow, ...manualSums};
      return {
        ...row,
        [this.totalRowKey]: controlFn(row),
        account_label_j1: "TOTAL CAPITAUX PROPRES",
      };
    },
    isTotalOk() {
      return this.totalRowComputed?.[this.totalRowKey] === "OK";
    },
  },
  watch: {
    testId: {
      handler() {
        this.getManualControl();
        this.getTestResults();
      },
    },
  },
  async mounted() {
    await this.getManualControl();
    await this.getTestResults();
  },
  methods: {
    getColumnsMeta,
    calculateEcart,
    async handleManualControl(
      row,
      manualInputColumn,
      newValue,
      isUpdated = false,
      tableName,
      zfirTableName = null
    ) {
      if (!isUpdated) return;
      let manualEntryData =
        this.manualControl?.[tableName] || this.manualControl;
      if (zfirTableName !== null)
        manualEntryData = manualEntryData[zfirTableName];
      row = {
        ...row,
        ...(manualEntryData?.[row?.aggregation_key] || {}),
        [manualInputColumn]: newValue,
      };
      await this.handleInput(row, tableName);
      return row;
    },
    async getManualControl() {
      const testManualInput = await ClosingFileService.getManualControl(
        this.testId,
        this.entityId,
        this.quarter_name
      );

      const detailManualInput = await ClosingFileService.getManualControl(
        "zfir20",
        this.entityId,
        this.quarter_name
      );
      this.manualControl = {
        ...testManualInput,
        zfir20: detailManualInput,
      };
    },
    calculateEcartWithManualInput(row, columnName, tableName) {
      const manualControlData =
        this.manualControl?.[tableName]?.[row.aggregation_key] || {};
      if (columnName === "test_i21_diff") {
        return row.balance - (
          safeParseFloat(manualControlData.man_input_i2_1) +
          safeParseFloat(manualControlData.man_input_i2_2) +
          safeParseFloat(manualControlData.man_input_i2_3) +
          safeParseFloat(manualControlData.man_input_i2_4) +
          safeParseFloat(manualControlData.man_input_i2_5)
        )
      }
      if (columnName === "test_i22_diff") {
        return row.balance - safeParseFloat(manualControlData.man_input_i2_6)
      }
      if (columnName === "test_i23_diff") {
        return row.balance - safeParseFloat(manualControlData.man_input_i2_7)
      }
      if (columnName === "test_b6_diff") {
        return (
          row.balance -
          manualControlData.man_input_b6_1 -
          manualControlData.man_input_b6_2
        );
      }
      if (columnName === 'test_b4_diff_1') {
        return (
          row.balance -
          safeParseFloat(manualControlData.man_amount_cca)
        )
      }
      if (columnName === 'test_d4_diff') {
        return (
          row.balance -
          safeParseFloat(manualControlData.man_amount_pca)
        )
      }
      if (columnName === "test_j32_diff") {
        return (
          row.balance +
          safeParseFloat(manualControlData.man_input_j32_immo_total)
        );
      }
      return calculateEcart({...row, ...manualControlData}, columnName);
    },
    performManualControl(row, index) {
      const manualControlData =
        this.manualControl?.[index] || this.manualControl;
      const controlFn =
        performManualControl[this.testId]?.[index] ||
        performManualControl[this.testId];
      if (!controlFn || typeof controlFn !== "function") return null;
      if (!manualControlData[row.aggregation_key]) {
        return "KO";
      }
      return controlFn({...row, ...manualControlData[row.aggregation_key]});
    },
    async getTestResults() {
      if (!this.testId) return;
      let detailTablesToDisplay = null;
      this.isLoading = true;
      this.manualControlColumns = MANUAL_CONTROL_COLUMNS[this.testId] || [];
      if (!Object.keys(DETAIL_TABLE_NAMES).includes(this.testId))
        detailTablesToDisplay = ["ZFIR20"];
      else detailTablesToDisplay = DETAIL_TABLE_NAMES[this.testId];
      this.testResults = await ClosingFileService.getTestTables(
        this.testId,
        this.entityId,
        this.quarter_name
      );
      if (detailTablesToDisplay) {
        for (const tableName of detailTablesToDisplay) {
          this.detailTables[tableName] = await ClosingFileService.getTestTables(
            tableName,
            this.entityId,
            this.quarter_name
          );
        }
      }
      if (this.hasTotalRow) {
        await this.fetchTotalRow();
      }
      this.isLoading = false;
      this.$emit("mounted", this.testResults?.control_passed || false);
    },
    async fetchTotalRow() {
      const table = this.testResults["0"];
      const {data} = await request({
        url: API_ENDPOINTS.lazyTable,
        query: {key: table.key, search_entity_code: this.totalCode},
      });
      this.totalRow = data[0];
    },
    getHeadings(tableData) {
      const filteredHeadings = {};
      if (!tableData?.columns) return COMMON_HEADINGS;
      tableData.columns.forEach((col) => {
        if (COMMON_HEADINGS[col.name]) filteredHeadings[col.name] = COMMON_HEADINGS[col.name];
      });
      return filteredHeadings;
    },
    getControlColumns(tableData) {
      const controlColumns = [];
      tableData.columns.forEach((col) => {
        if (col.name.startsWith("test_")) controlColumns.push(col.name);
      });
      return controlColumns;
    },
    async handleInput(row, tableName) {
      const isZfirTable = tableName.toLowerCase().includes("zfir");
      const updatedRow = isZfirTable
        ? this.$selectObjectKeys(
          row,
          ...[
            ...MANUAL_CONTROL_DETAIL_COLUMNS[this.testId],
            "aggregation_key",
          ]
        )
        : row;
      await ClosingFileService.updateManualControl(
        updatedRow,
        isZfirTable ? tableName : this.testId,
        this.entityId,
        this.quarter_name,
        isZfirTable ? "0" : tableName
      );
      await this.getManualControl();
    },
    getFilteredColumns() {
      return Object.keys(this.getInitFilters() || {}).map((col) =>
        col.replace("search_", "")
      );
    },
    getInitFilters(tableName) {
      if (tableName === 'ZFIR23') {
        switch (this.testId) {
          case 'B7':
            return {search_status: 'p'}
          default:
            return {}
        }
      }

      let index = this.selectedRow.table_index;
      let filtersObject = { search_entity_code: this.entityId };
      if (this.column_name && ['balance_6', 'balance_7'].includes(this.column_name)){
        filtersObject[`search_account`] = `^${this.column_name.slice(-1)}`
      }
      const filtersForTest = TABLE_FILTERS[this.testId];
      if (!filtersForTest) return filtersObject;
      const filterData = Array.isArray(filtersForTest)
        ? filtersForTest
        : filtersForTest?.[index];
      if (!filterData || !this.selectedRow) return filtersObject;
      filterData.forEach((filterColumn) => {
        if (this.selectedRow[filterColumn] !== undefined) {
          filtersObject[`search_${filterColumn}`] =
            this.selectedRow[filterColumn];
        }
      });
      if (["02", "D1.1"].includes(this.testId))
        filtersObject["search_account_root_1__in"] = ["6", "7"];
      if (this.testId === "F1" && filterData.includes("asset_category")) {
        if (this.selectedRow["asset_category"] === "68111000" || this.selectedRow["asset_category"] === "68112000") {
          delete filtersObject["search_asset_category"];
        } else if (this.selectedRow["asset_category"].startsWith("28")) {
          let assetCategory = this.selectedRow["asset_category"];
          filtersObject["search_asset_category"] = "21" + assetCategory.slice(3, -1) + assetCategory.slice(-1).repeat(2);
        }
      }
      return filtersObject;
    },
    handleClickedRow(row, restrictedName = null) {
      if (restrictedName === "immo_total") {
        row.asset_category = row[ACCOUNT];
        delete row[ACCOUNT];
      }
      this.selectedRow = row;
      this.restrictedDetailsIndex =
        detailsRestricted[this.testId]?.[restrictedName];
      this.showDetails = true;
      this.column_name = restrictedName
    },
  },
};
